'use client';

import { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import useGetUsageDashboard from './apis/use-get-usage-dashboard';
import useGetUsageDashboardGraph from './apis/use-get-usage-dashboard-graph';
import useGetUserStats from './apis/use-get-user-stats';
import { format } from 'date-fns';
import { JarvisDateInput } from '@components/jarvis-ui/JarvisDateInput';
import { useStaffAccess } from '@hooks/useStaffAccess';
import usePpjkOptions from '@/features/ppjk-picker/usePpjkOptions';
import { Text, Loader, Select, Tabs } from '@mantine/core';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { IconCalendar, IconChartLine, IconChartBar, IconActivity } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import SharedDataTable from '@components/shared-table/shared-data-table';
import type { DataTableColumn } from 'mantine-datatable';
import { useTheme } from '@contexts/ThemeContext';

// Lazy load heavy chart components for better performance
const UsageDashboardGraph = lazy(() => import('./components/UsageDashboardGraph'));
const UserStatsGraph = lazy(() => import('./components/UserStatsGraph'));

export default function DashboardPage() {
  const { mode, colors: themeColors } = useTheme();
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 6);
    return date;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  });

  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('tanggalDibuat');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Tab state
  const [activeTab, setActiveTab] = useState<string>('graph');

  // PPJK selection state
  const [selectedPpjk, setSelectedPpjk] = useState<string>('');

  // Super admins (GET /cms/me) get the PPJK picker.
  const { superAdmin: isAdmin } = useStaffAccess();

  // The picker works in NPWPs: it opens on the caller's own PPJK and sends
  // the picked one as `ppjkNpwp`. The server honours that for super admins
  // only and resolves the name itself; everyone else always gets their own.
  const defaultPpjk = useMemo(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('nomor_ppjk') || '';
    }
    return '';
  }, []);

  // Set default PPJK on mount
  useEffect(() => {
    setSelectedPpjk(defaultPpjk);
  }, [defaultPpjk]);

  const { options: ppjkOptions } = usePpjkOptions(defaultPpjk, isAdmin);

  const dashboardParams = useMemo(
    () => ({
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      offset: currentPage - 1,
      limit: pageSize,
      orderBy: sortBy,
      ascending: sortOrder === 'asc',
      ppjkNpwp: selectedPpjk || undefined,
    }),
    [startDate, endDate, currentPage, pageSize, sortBy, sortOrder, selectedPpjk]
  );

  const {
    data: usageData,
    totalCount,
    loading: usageLoading,
    error: usageError,
  } = useGetUsageDashboard(dashboardParams);

  // Graph data (without pagination)
  const graphParams = useMemo(
    () => ({
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      ppjkNpwp: selectedPpjk || undefined,
    }),
    [startDate, endDate, selectedPpjk]
  );

  const { data: graphData, loading: graphLoading, error: graphError } = useGetUsageDashboardGraph(graphParams);

  // User Stats data (without pagination for graph view)
  const userStatsParams = useMemo(
    () => ({
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      offset: 0,
      limit: 100, // Get top 100 users for comprehensive stats
      orderBy: 'totalAju',
      ascending: false,
      ppjkNpwp: selectedPpjk || undefined,
    }),
    [startDate, endDate, selectedPpjk]
  );

  const { data: userStatsData, loading: userStatsLoading, error: userStatsError } = useGetUserStats(userStatsParams);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: string | null) => {
    if (size) {
      setPageSize(Number.parseInt(size));
      setCurrentPage(1);
    }
  };

  const handlePpjkChange = (value: string | null) => {
    setSelectedPpjk(value || defaultPpjk);
    setCurrentPage(1); // Reset to first page when PPJK changes
  };

  // Define the type for usage data items
  type UsageDataItem = {
    noAju: string;
    ppjkName: string;
    tanggalDibuat: string;
    namaPerusahaan: string;
    userName: string;
    totalBarang: number;
  };

  // Define columns for SharedDataTable
  const columns: DataTableColumn<UsageDataItem>[] = [
    {
      accessor: 'noAju',
      title: 'No. AJU',
      textAlign: 'left',
      width: 130,
      sortable: true,
      render: (item) => (
        <span className="text-xs font-medium" style={{ color: themeColors.primary }}>
          {item.noAju}
        </span>
      ),
    },
    {
      accessor: 'ppjkName',
      title: 'PPJK',
      textAlign: 'left',
      width: 90,
      sortable: true,
      render: (item) => (
        <span className="text-xs" style={{ color: themeColors.text }}>
          {item.ppjkName}
        </span>
      ),
    },
    {
      accessor: 'tanggalDibuat',
      title: 'Tanggal',
      textAlign: 'center',
      width: 110,
      sortable: true,
      render: (item) => (
        <span className="text-xs text-center block" style={{ color: themeColors.text }}>
          {new Date(item.tanggalDibuat).toLocaleDateString('id-ID', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      accessor: 'namaPerusahaan',
      title: 'Perusahaan',
      textAlign: 'left',
      width: 160,
      sortable: true,
      render: (item) => (
        <span className="text-xs truncate block" style={{ color: themeColors.text }}>
          {item.namaPerusahaan}
        </span>
      ),
    },
    {
      accessor: 'userName',
      title: 'User',
      textAlign: 'left',
      width: 120,
      sortable: true,
      render: (item) => (
        <span className="text-xs" style={{ color: themeColors.text }}>
          {item.userName}
        </span>
      ),
    },
    {
      accessor: 'totalBarang',
      title: 'Items',
      textAlign: 'center',
      width: 80,
      sortable: true,
      render: (item) => (
        <div className="text-center">
          <Badge
            className="text-[10px] px-2 py-0.5"
            style={{
              backgroundColor: themeColors.primary,
              color: 'white',
            }}
          >
            {item.totalBarang}
          </Badge>
        </div>
      ),
    },
  ];

  if (usageLoading && graphLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor: themeColors.background }}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-[3px]" style={{ borderColor: `${themeColors.border}40` }} />
            <div
              className="absolute inset-0 w-12 h-12 rounded-full border-[3px] border-transparent animate-spin"
              style={{ borderTopColor: themeColors.primary }}
            />
          </div>
          <p className="text-sm font-medium animate-pulse" style={{ color: themeColors.textSecondary }}>
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (usageError && graphError) {
    return (
      <div
        className="flex items-center justify-center min-h-[400px]"
        style={{ backgroundColor: themeColors.background }}
      >
        <Card className="border" style={{ backgroundColor: themeColors.card, borderColor: themeColors.border }}>
          <CardContent className="p-6">
            <p className="text-sm" style={{ color: themeColors.error }}>
              Error: {usageError || graphError}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="min-h-screen" style={{ backgroundColor: themeColors.background }}>
      <div className="w-full">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
                style={{ backgroundColor: `${themeColors.primary}15` }}
              >
                <IconActivity size={22} style={{ color: themeColors.primary }} />
              </span>
              <div>
                <h1
                  className="text-2xl font-bold tracking-tight"
                  style={{ color: themeColors.text, fontFamily: "'Libre Baskerville', serif" }}
                >
                  Operational Dashboard
                </h1>
                <p className="text-sm mt-0.5 whitespace-nowrap" style={{ color: themeColors.textSecondary }}>
                  Overview aktivitas, performa, dan tren operasional
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {isAdmin && (
                <Select
                  value={selectedPpjk}
                  onChange={handlePpjkChange}
                  data={ppjkOptions}
                  placeholder="Select PPJK"
                  size="sm"
                  searchable
                  allowDeselect={false}
                  nothingFoundMessage="PPJK tidak ditemukan"
                  style={{ minWidth: 420 }}
                  styles={{
                    input: {
                      backgroundColor: themeColors.formFields,
                      borderColor: themeColors.border,
                      color: themeColors.text,
                    },
                  }}
                />
              )}
              <JarvisDateInput
                value={startDate}
                onChange={setStartDate}
                placeholder="Pick start date"
                leftSection={<IconCalendar size={16} />}
              />
              <JarvisDateInput
                value={endDate}
                onChange={setEndDate}
                placeholder="Pick end date"
                leftSection={<IconCalendar size={16} />}
              />
            </div>
          </div>
        </motion.div>

        <Separator className="mb-6" style={{ backgroundColor: themeColors.border }} />

        {/* Tabbed Interface */}
        <motion.div
          data-tour="dashboard-stats"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'graph')}>
            <Tabs.List
              style={{
                borderBottomColor: themeColors.border,
              }}
            >
              <Tabs.Tab value="graph" leftSection={<IconChartLine size={16} />}>
                Graph View
              </Tabs.Tab>
              <Tabs.Tab value="userstats" leftSection={<IconChartBar size={16} />}>
                User Stats
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="graph" pt="md">
              <Suspense
                fallback={
                  <div className="flex items-center justify-center p-12">
                    <div className="flex flex-col items-center gap-3">
                      <Loader size="md" style={{ color: themeColors.primary }} />
                      <p className="text-xs" style={{ color: themeColors.textSecondary }}>
                        Loading charts...
                      </p>
                    </div>
                  </div>
                }
              >
                <UsageDashboardGraph data={graphData} loading={graphLoading} error={graphError} />
              </Suspense>
            </Tabs.Panel>

            <Tabs.Panel value="userstats" pt="md">
              <Suspense
                fallback={
                  <div className="flex items-center justify-center p-12">
                    <div className="flex flex-col items-center gap-3">
                      <Loader size="md" style={{ color: themeColors.primary }} />
                      <p className="text-xs" style={{ color: themeColors.textSecondary }}>
                        Loading stats...
                      </p>
                    </div>
                  </div>
                }
              >
                <UserStatsGraph data={userStatsData} loading={userStatsLoading} error={userStatsError} />
              </Suspense>
            </Tabs.Panel>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
